import { useState, useEffect, useCallback } from 'react';
import { s, authHeaders, Toast } from './listPageStyles';
import { ListPageShell } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import './securityScreens.css';

// G-Y-01 — Yetki Şablonları. İki panelli: sol şablon listesi, sağ satır + atama editörü.
//
// KK-01: şablon bir yetki KAYNAĞIDIR, kısıt değil. Atanan şablonların hakları diğer kaynaklarla
// birleşimle toplanır; şablon hiçbir hakkı geri alamaz. Ekran bunu açıkça söylüyor, yoksa
// "şablonu kaldırdım ama yetki duruyor" beklentisi oluşur.

const SCOPES = [
  { value: 1, label: 'Menü' },
  { value: 2, label: 'Aksiyon' },
  { value: 3, label: 'Kolon' },
  { value: 4, label: 'Karma' },
];

const ITEM_TYPES = [
  { value: 1, label: 'Menü' },
  { value: 2, label: 'Aksiyon' },
  { value: 3, label: 'Kolon' },
  { value: 4, label: 'Rapor' },
];

const PERMISSIONS = [
  { value: 0, label: 'Kapalı', tone: 'danger' },
  { value: 1, label: 'Açık', tone: 'ok' },
  { value: 2, label: 'Salt-okunur', tone: 'warn' },
  { value: 3, label: 'Gizli', tone: 'danger' },
];

const ACTIONS = [
  { value: 1, label: 'Ekle' }, { value: 2, label: 'Güncelle' },
  { value: 3, label: 'Sil' }, { value: 4, label: 'Kopyala' }, { value: 17, label: 'Görüntüle' },
];

const TARGET_TYPES = [{ value: 1, label: 'Rol' }, { value: 3, label: 'Kullanıcı' }];

const emptyForm = { code: '', name: '', description: '', scope: 4, isLocked: false, isActive: true };

export function PermissionTemplatesPage() {
  usePageMeta('Yetki Şablonları', 'Kullanıcı & Yetki');
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [isNew, setIsNew] = useState(false);
  const [menus, setMenus] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };

  const pull = async (url) => {
    try {
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) return [];
      const j = await res.json();
      return j?.value || j?.data || j?.Data || (Array.isArray(j) ? j : []);
    } catch { return []; }
  };

  const loadList = useCallback(async () => {
    setLoading(true);
    const rows = await pull('/api/permission-templates?$orderby=Code&$top=500');
    setTemplates(rows);
    setLoading(false);
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [m, r, u] = await Promise.all([
        pull('/api/Menu/MenuTree'),
        pull('/api/Roles/Select'),
        pull('/api/Users?$select=Id,FirstName,LastName,Email&$orderby=FirstName&$top=1000'),
      ]);
      if (cancelled) return;
      const flat = [];
      const walk = (nodes) => (nodes || []).forEach((n) => {
        const id = n.id ?? n.Id; const path = n.path ?? n.Path;
        const title = n.title ?? n.Title ?? n.name ?? n.Name;
        if (id && path && path !== '#') flat.push({ id, label: `${title} (${id})` });
        walk(n.children ?? n.Children);
      });
      walk(m);
      setMenus(flat.sort((a, b) => a.label.localeCompare(b.label, 'tr')));
      setRoles((r || []).map((x) => ({ id: x.id ?? x.Id, label: x.name ?? x.Name })).filter((x) => x.id));
      setUsers((u || []).map((x) => ({
        id: x.id ?? x.Id,
        label: `${x.firstName ?? x.FirstName ?? ''} ${x.lastName ?? x.LastName ?? ''}`.trim() || (x.email ?? x.Email ?? ''),
      })).filter((x) => x.id));
    })();
    return () => { cancelled = true; };
  }, []);

  const openTemplate = async (id) => {
    setIsNew(false);
    setSelectedId(id);
    try {
      const res = await fetch(`/api/permission-templates/${id}`, { headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Şablon okunamadı');
      const d = body?.data || body?.Data;
      setDetail(d);
      setForm({
        code: d.code, name: d.name, description: d.description || '',
        scope: d.scope, isLocked: d.isLocked, isActive: d.isActive,
      });
    } catch (e) {
      showToast('error', e.message || 'Şablon okunamadı');
    }
  };

  const openNew = () => {
    setIsNew(true);
    setSelectedId(null);
    setDetail({ items: [], assignments: [], isSystem: false, isLocked: false, itemsHidden: false });
    setForm(emptyForm);
  };

  const saveTemplate = async () => {
    setSaving(true);
    try {
      const url = isNew ? '/api/permission-templates' : `/api/permission-templates/${selectedId}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT', headers: authHeaders(), body: JSON.stringify(form),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Kaydedilemedi');
      showToast('success', 'Şablon kaydedildi');
      const savedId = body?.data?.id ?? body?.Data?.Id ?? selectedId;
      await loadList();
      if (savedId) await openTemplate(savedId);
    } catch (e) {
      showToast('error', e.message || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const saveItems = async () => {
    setSaving(true);
    try {
      const res = await fetch(`/api/permission-templates/${selectedId}/items`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify(detail.items || []),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Satırlar kaydedilemedi');
      showToast('success', 'Şablon satırları kaydedildi');
      openTemplate(selectedId);
    } catch (e) {
      showToast('error', e.message || 'Satırlar kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const saveAssignments = async () => {
    setSaving(true);
    try {
      const res = await fetch(`/api/permission-templates/${selectedId}/assign`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify(detail.assignments || []),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Atamalar kaydedilemedi');
      showToast('success', 'Atamalar kaydedildi');
      openTemplate(selectedId);
    } catch (e) {
      showToast('error', e.message || 'Atamalar kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const removeTemplate = async () => {
    if (!selectedId) return;
    try {
      const res = await fetch(`/api/permission-templates/${selectedId}`, { method: 'DELETE', headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Silinemedi');
      showToast('success', 'Şablon silindi');
      setSelectedId(null); setDetail(null);
      loadList();
    } catch (e) {
      showToast('error', e.message || 'Silinemedi');
    }
  };

  const addItem = () => setDetail((d) => ({
    ...d, items: [...(d.items || []), { id: 0, itemType: 1, menuId: null, actionTypeId: null, fieldKey: '', permission: 1, isLockedRow: false }],
  }));
  const changeItem = (i, patch) => setDetail((d) => ({
    ...d, items: d.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)),
  }));
  const removeItem = (i) => setDetail((d) => ({ ...d, items: d.items.filter((_, idx) => idx !== i) }));

  const addAssignment = () => setDetail((d) => ({
    ...d, assignments: [...(d.assignments || []), { id: 0, targetType: 1, targetId: '', validFrom: null, validTo: null }],
  }));
  const changeAssignment = (i, patch) => setDetail((d) => ({
    ...d, assignments: d.assignments.map((a, idx) => (idx === i ? { ...a, ...patch } : a)),
  }));
  const removeAssignment = (i) => setDetail((d) => ({ ...d, assignments: d.assignments.filter((_, idx) => idx !== i) }));

  const readOnly = !canEdit || detail?.isLocked;

  return (
    <div style={s.container}>
      <ListPageShell
        title="Yetki Şablonları"
        subtitle="Menü, aksiyon ve kolon yetkilerini tek tanımda toplayıp rol veya kullanıcılara atayın."
        actions={canAdd && <button style={s.btnPrimary} onClick={openNew}>+ Yeni Şablon</button>}
      >
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Sol panel — şablon listesi */}
          <div className="sec-card" style={{ flex: '0 0 280px', maxWidth: 300 }}>
            <div className="sec-card-label">Şablonlar</div>
            <div className="sec-dist" style={{ marginTop: 9, maxHeight: 460, overflowY: 'auto' }}>
              {loading && <div className="sec-card-hint">Yükleniyor…</div>}
              {!loading && templates.length === 0 && <div className="sec-card-hint">Henüz şablon yok</div>}
              {templates.map((t) => {
                const id = t.id ?? t.Id;
                return (
                  <button
                    key={id}
                    type="button"
                    className={`sec-tab${selectedId === id ? ' on' : ''}`}
                    style={{ justifyContent: 'flex-start', textAlign: 'left', width: '100%', height: 'auto', padding: '8px 11px' }}
                    onClick={() => openTemplate(id)}
                  >
                    <span>
                      <span className="c360-erp-chip">{t.code ?? t.Code}</span>{' '}
                      {t.name ?? t.Name}
                      {(t.isSystem ?? t.IsSystem) && <span className="sec-badge sec-badge--info" style={{ marginLeft: 6 }}>SİSTEM</span>}
                      {(t.isLocked ?? t.IsLocked) && <span className="sec-badge sec-badge--muted" style={{ marginLeft: 6 }}>KİLİTLİ</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sağ panel — editör */}
          <div style={{ flex: '1 1 460px', minWidth: 380 }}>
            {!detail ? (
              <div className="sec-card">
                <div className="sec-card-hint">
                  Soldan bir şablon seçin veya yeni şablon oluşturun. Şablon bir yetki <strong>kaynağıdır</strong>:
                  atadığı haklar diğer kaynaklarla birleşir, hiçbir hakkı geri almaz.
                </div>
              </div>
            ) : (
              <>
                <div className="sec-card" style={{ marginBottom: 13 }}>
                  <div className="sec-card-label">{isNew ? 'Yeni şablon' : 'Şablon bilgileri'}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9, alignItems: 'flex-end', marginTop: 9 }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span className="sec-card-label">Kod</span>
                      <span className="gp-num">
                        <input type="text" value={form.code} disabled={!canEdit || detail.isSystem}
                          onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} />
                      </span>
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span className="sec-card-label">Ad</span>
                      <span className="gp-num">
                        <input type="text" value={form.name} disabled={!canEdit}
                          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                      </span>
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span className="sec-card-label">Kapsam</span>
                      <select className="gp-select" value={form.scope} disabled={!canEdit}
                        onChange={(e) => setForm((f) => ({ ...f, scope: Number(e.target.value) }))}>
                        {SCOPES.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
                      </select>
                    </label>
                    <label className="sec-inline-check" style={{ marginTop: 0 }}>
                      <input type="checkbox" checked={form.isLocked} disabled={!canEdit}
                        onChange={(e) => setForm((f) => ({ ...f, isLocked: e.target.checked }))} />
                      Kilitli (satırları gizlenir)
                    </label>
                    <label className="sec-inline-check" style={{ marginTop: 0 }}>
                      <input type="checkbox" checked={form.isActive} disabled={!canEdit}
                        onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} />
                      Aktif
                    </label>
                    {canEdit && (
                      <button type="button" style={s.btnPrimary} disabled={saving} onClick={saveTemplate}>
                        {saving ? 'Kaydediliyor…' : 'Kaydet'}
                      </button>
                    )}
                    {canDelete && !isNew && !detail.isSystem && (
                      <button type="button" style={s.btnDanger} onClick={removeTemplate}>Sil</button>
                    )}
                  </div>
                </div>

                {isNew ? (
                  <div className="sec-card">
                    <div className="sec-card-hint">Satır ve atama eklemek için önce şablonu kaydedin.</div>
                  </div>
                ) : (
                  <>
                    <div className="sec-spec-group">Şablon satırları</div>
                    {detail.itemsHidden ? (
                      <div className="sec-card">
                        <div className="sec-card-hint">
                          Bu şablon <strong>kilitli</strong>. Satırları bilerek gizlenmiştir —
                          değiştiremeyeceğiniz tanımlarla uğraşmamanız için.
                        </div>
                      </div>
                    ) : (
                      <>
                        <table className="c360-table" style={{ width: '100%' }}>
                          <thead>
                            <tr><th style={{ width: 110 }}>Tip</th><th>Ekran</th><th style={{ width: 130 }}>Aksiyon / Alan</th>
                              <th style={{ width: 140 }}>İzin</th><th style={{ width: 80 }}>İşlem</th></tr>
                          </thead>
                          <tbody>
                            {(detail.items || []).length === 0 && (
                              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 18 }}>Satır yok</td></tr>
                            )}
                            {(detail.items || []).map((it, i) => (
                              <tr key={i}>
                                <td>
                                  <select className="gp-select" value={it.itemType} disabled={readOnly}
                                    onChange={(e) => changeItem(i, { itemType: Number(e.target.value) })}>
                                    {ITEM_TYPES.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
                                  </select>
                                </td>
                                <td>
                                  <select className="gp-select" value={it.menuId ?? ''} disabled={readOnly}
                                    onChange={(e) => changeItem(i, { menuId: e.target.value ? Number(e.target.value) : null })}>
                                    <option value="">—</option>
                                    {menus.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                                  </select>
                                </td>
                                <td>
                                  {it.itemType === 2 ? (
                                    <select className="gp-select" value={it.actionTypeId ?? ''} disabled={readOnly}
                                      onChange={(e) => changeItem(i, { actionTypeId: e.target.value ? Number(e.target.value) : null })}>
                                      <option value="">—</option>
                                      {ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                                    </select>
                                  ) : it.itemType === 3 ? (
                                    <span className="gp-num">
                                      <input type="text" value={it.fieldKey || ''} disabled={readOnly} placeholder="alan anahtarı"
                                        onChange={(e) => changeItem(i, { fieldKey: e.target.value })} />
                                    </span>
                                  ) : '—'}
                                </td>
                                <td>
                                  <select className="gp-select" value={it.permission} disabled={readOnly}
                                    onChange={(e) => changeItem(i, { permission: Number(e.target.value) })}>
                                    {PERMISSIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                                  </select>
                                </td>
                                <td>
                                  {it.isLockedRow
                                    ? <span className="sec-badge sec-badge--muted">KİLİTLİ</span>
                                    : !readOnly && <button type="button" style={s.btnDanger} onClick={() => removeItem(i)}>Sil</button>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {!readOnly && (
                          <div style={{ display: 'flex', gap: 8, marginTop: 9 }}>
                            <button type="button" style={s.btnSecondary} onClick={addItem}>+ Satır ekle</button>
                            <button type="button" style={s.btnPrimary} disabled={saving} onClick={saveItems}>
                              {saving ? 'Kaydediliyor…' : 'Satırları kaydet'}
                            </button>
                          </div>
                        )}
                      </>
                    )}

                    <div className="sec-spec-group">Atamalar</div>
                    <div className="sec-card-hint" style={{ marginBottom: 8 }}>
                      Kullanıcı grubuna atama yapılamaz — grup yalnız sınıflandırma tanımıdır, yetki taşımaz.
                    </div>
                    <table className="c360-table" style={{ width: '100%' }}>
                      <thead><tr><th style={{ width: 130 }}>Hedef tipi</th><th>Hedef</th><th style={{ width: 80 }}>İşlem</th></tr></thead>
                      <tbody>
                        {(detail.assignments || []).length === 0 && (
                          <tr><td colSpan={3} style={{ textAlign: 'center', padding: 18 }}>Atama yok</td></tr>
                        )}
                        {(detail.assignments || []).map((a, i) => (
                          <tr key={i}>
                            <td>
                              <select className="gp-select" value={a.targetType} disabled={!canEdit}
                                onChange={(e) => changeAssignment(i, { targetType: Number(e.target.value), targetId: '' })}>
                                {TARGET_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                              </select>
                            </td>
                            <td>
                              <select className="gp-select" value={a.targetId} disabled={!canEdit}
                                onChange={(e) => changeAssignment(i, { targetId: Number(e.target.value) })}>
                                <option value="">Seçiniz…</option>
                                {(a.targetType === 1 ? roles : users).map((o) => (
                                  <option key={o.id} value={o.id}>{o.label}</option>
                                ))}
                              </select>
                            </td>
                            <td>{canEdit && <button type="button" style={s.btnDanger} onClick={() => removeAssignment(i)}>Sil</button>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {canEdit && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 9 }}>
                        <button type="button" style={s.btnSecondary} onClick={addAssignment}>+ Atama ekle</button>
                        <button type="button" style={s.btnPrimary} disabled={saving} onClick={saveAssignments}>
                          {saving ? 'Kaydediliyor…' : 'Atamaları kaydet'}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </ListPageShell>
      <Toast toast={toast} />
    </div>
  );
}

export default PermissionTemplatesPage;
